// Context: This is a temporary solution to sanitize the settings
// There were no good solutions to ensure backwards compatibility
// of the widget date filters, so we sanitize the settings to slowly migrate
// dates to the new format as users save their dashboards
import type { PropertyFilterToken } from '@cloudscape-design/collection-hooks';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import type { GigawidgetSettings } from './util';

dayjs.extend(customParseFormat);

export const sanitizeTokens = (
  tokens: PropertyFilterToken[]
): PropertyFilterToken[] => {
  return tokens.map((token) => {
    if (dayjs(token?.value, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
      const startOfTokenDate = dayjs(token.value).startOf('day');
      let startDate = null;
      let endDate = null;

      if (token.operator === '>=') {
        startDate = startOfTokenDate.format('YYYY-MM-DD');
        endDate = startOfTokenDate.add(10, 'year').format('YYYY-MM-DD');
      }

      if (token.operator === '>') {
        startDate = startOfTokenDate.add(1, 'day').format('YYYY-MM-DD');
        endDate = startOfTokenDate.add(10, 'year').format('YYYY-MM-DD');
      }

      if (token.operator === '<=') {
        startDate = startOfTokenDate.subtract(10, 'year').format('YYYY-MM-DD');
        endDate = startOfTokenDate.format('YYYY-MM-DD');
      }

      if (token.operator === '<') {
        startDate = startOfTokenDate.subtract(10, 'year').format('YYYY-MM-DD');
        endDate = startOfTokenDate.subtract(1, 'day').format('YYYY-MM-DD');
      }

      if (token.operator === '=') {
        startDate = startOfTokenDate.format('YYYY-MM-DD');
        endDate = dayjs(token.value).endOf('day').format('YYYY-MM-DD');
      }

      const newTokenValue = {
        type: 'absolute',
        startDate,
        endDate,
      };

      return {
        propertyKey: token.propertyKey,
        operator: '=',
        value: newTokenValue,
      };
    }

    return token;
  });
};

export const sanitizeSettings = (
  settings: GigawidgetSettings
): GigawidgetSettings => {
  // Context: This is here as only some of the dashboards currently use the new date format
  // TODO: remove this guard once we migrate all registers to the new date format
  if (
    !settings?.dataSource ||
    !parentTypesMigratedToRelativeDates.includes(
      settings.dataSource as Parent_Type_Enum
    )
  ) {
    return settings;
  }

  if (settings?.filtering?.tokens?.length ?? 0 > 0) {
    return {
      ...settings,
      filtering: {
        ...settings.filtering,
        tokens: sanitizeTokens(
          settings?.filtering?.tokens as PropertyFilterToken[]
        ),
      },
    } as GigawidgetSettings;
  }

  return settings;
};

export const parentTypesMigratedToRelativeDates: Parent_Type_Enum[] = [
  Parent_Type_Enum.Acceptance,
  Parent_Type_Enum.Action,
  Parent_Type_Enum.Assessment,
  Parent_Type_Enum.AssessmentActivity,
  Parent_Type_Enum.AttestationRecord,
  Parent_Type_Enum.Cause,
  Parent_Type_Enum.ChangeRequest,
  Parent_Type_Enum.ComplianceMonitoringAssessment,
  Parent_Type_Enum.Control,
  Parent_Type_Enum.Document,
  Parent_Type_Enum.Indicator,
  Parent_Type_Enum.InternalAuditEntity,
  Parent_Type_Enum.Issue,
  Parent_Type_Enum.MyItems,
  Parent_Type_Enum.Obligation,
  Parent_Type_Enum.Risk,
  Parent_Type_Enum.TestResult,
  Parent_Type_Enum.ThirdParty,
];
